import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Copy, ArrowsClockwise, Key, Lightning, ArrowRight,
  Compass, ChartLineUp, Robot, Sparkle, ChatCircleDots, MagicWand,
} from "@phosphor-icons/react";
import { Reveal, MaskLine, Parallax, EASE } from "@/lib/anim";
import ScrollExpandMedia from "@/components/ui/scroll-expansion-hero";

const CHAPTERS = [
  { n: "01", icon: Copy, accent: "magenta", title: "Someone else's assumptions", body: "Every template starts with someone else's layout, someone else's workflow, and someone else's idea of what your customer wants. You bend your business to fit the software.", motif: "same" },
  { n: "02", icon: ArrowsClockwise, accent: "cobalt", title: "Custom flips that", body: "The software bends to fit your business. Your process. Your customer journey. Nothing you don't need, everything you do.", motif: "flip" },
  { n: "03", icon: Key, accent: "teal", title: "You own what we build", body: "No platform lock-in, no hostage fees, no fine print. The thing we build is yours — to run, to grow, to extend.", motif: "own" },
];

const ACCENT = {
  magenta: { tile: "from-magenta/30 to-magenta/5 border-magenta/30 text-magenta", glow: "group-hover:shadow-[0_0_40px_-10px_rgba(236,72,153,0.6)]", dot: "bg-magenta" },
  cobalt: { tile: "from-cobalt/30 to-cobalt/5 border-cobalt/30 text-cobalt", glow: "group-hover:shadow-[0_0_40px_-10px_rgba(59,110,255,0.6)]", dot: "bg-cobalt" },
  teal: { tile: "from-teal/30 to-teal/5 border-teal/30 text-teal", glow: "group-hover:shadow-[0_0_40px_-10px_rgba(64,224,208,0.6)]", dot: "bg-teal" },
};

const NAV = ["Home", "Services", "About", "Contact"];
const FEATURES = [
  { icon: Compass, t: "Strategy", d: "Plans that fit your goals" },
  { icon: ChartLineUp, t: "Analytics", d: "Know what actually works" },
  { icon: Robot, t: "Automation", d: "Less busywork, more growth" },
];
const serif = { fontFamily: "Georgia, serif" };

const Motif = ({ type }) => {
  if (type === "same") return (
    <div className="flex items-center gap-2">
      {[0, 1, 2].map((i) => (<motion.div key={i} initial={{ opacity: 0.3 }} whileInView={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }} className="h-8 w-8 rounded-md bg-white/10 border border-white/10" />))}
      <span className="ml-1 text-xs text-white/30">identical</span>
    </div>
  );
  if (type === "flip") return (
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-md bg-white/10 border border-white/10" />
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 5, repeat: Infinity, ease: "linear" }} className="text-cobalt"><ArrowsClockwise size={18} weight="bold" /></motion.div>
      <div className="h-8 w-8 rounded-md bg-gradient-to-tr from-teal to-cobalt" />
      <div className="h-8 w-8 rounded-md bg-gradient-to-tr from-cobalt to-violet" />
    </div>
  );
  return (
    <div className="flex items-center gap-2">
      <motion.div animate={{ y: [0, -3, 0] }} transition={{ duration: 2.4, repeat: Infinity }} className="flex items-center gap-1.5 rounded-full border border-teal/30 bg-teal/10 px-3 py-1.5 text-teal text-xs"><Key size={14} weight="fill" /> 100% yours</motion.div>
      <span className="text-xs text-white/30 line-through">lock-in</span>
    </div>
  );
};

// ---- REALISTIC TEMPLATE SITE (classic centered corporate layout) ----
const TemplateLayout = () => (
  <div className="absolute inset-0 bg-white overflow-hidden flex flex-col" style={serif}>
    {/* utility bar */}
    <div className="h-7 bg-[#1f2740] text-white/60 text-[10px] flex items-center justify-end px-6 gap-4">
      <span>Mon–Fri 9:00–17:00</span><span>info@northwindco.com</span><span>+1 (555) 010-2020</span>
    </div>
    {/* nav */}
    <div className="h-14 bg-white border-b border-[#e6e6e0] flex items-center justify-between px-6">
      <span className="text-[#1f2740] font-bold text-lg">Northwind &amp; Co.</span>
      <div className="flex items-center gap-5">
        {NAV.map((n) => <span key={n} className="text-[#4a4a4a] text-xs">{n}</span>)}
        <span className="text-white text-xs bg-[#2b57b8] rounded px-3 py-1.5">Get a Quote</span>
      </div>
    </div>
    {/* hero with a real photo — looks like an actual corporate website */}
    <div className="relative flex-1 min-h-0">
      <img src="/manifesto/office.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0d1830]/90 via-[#0d1830]/60 to-[#0d1830]/10" />
      <div className="relative h-full flex flex-col justify-center px-10 md:px-16 max-w-2xl">
        <div className="text-[#8fb4ff] text-[10px] md:text-xs tracking-[0.2em] uppercase">Welcome to Northwind &amp; Co.</div>
        <h3 className="mt-2 text-white font-extrabold text-3xl md:text-5xl leading-[1.05]">Grow your business with confidence.</h3>
        <p className="mt-3 text-white/80 text-sm md:text-base max-w-md">One trusted partner for strategy, analytics and automation — helping companies scale since 2004.</p>
        <div className="mt-6 flex gap-3">
          <span className="text-white text-sm bg-[#2b57b8] rounded px-5 py-2.5 font-semibold">Get started</span>
          <span className="text-white text-sm border border-white/50 rounded px-5 py-2.5">Learn more</span>
        </div>
      </div>
    </div>
    {/* trusted by */}
    <div className="flex items-center justify-center gap-4 py-2.5 border-y border-[#eee] bg-[#fafafa]">
      <span className="text-[10px] text-[#9a9a9a] uppercase tracking-wider">Trusted by</span>
      {[0, 1, 2, 3].map((i) => <span key={i} className="h-3 w-12 rounded bg-[#dcdcd6]" />)}
    </div>
    {/* feature columns */}
    <div className="grid grid-cols-3 gap-4 px-8 py-8">
      {FEATURES.map((f) => { const Icon = f.icon; return (
        <div key={f.t} className="text-center">
          <div className="mx-auto h-11 w-11 rounded-full bg-[#eef2f7] text-[#2b57b8] flex items-center justify-center"><Icon size={18} /></div>
          <div className="mt-2 text-sm font-semibold text-[#1f2740]">{f.t}</div>
          <div className="text-xs text-[#7a7a7a]">{f.d}</div>
        </div>
      ); })}
    </div>
    {/* footer */}
    <div className="mt-auto h-9 bg-[#1f2740] text-white/55 text-[10px] flex items-center justify-between px-6">
      <span>© 2026 Northwind &amp; Co.</span><span>Privacy · Terms · Sitemap</span>
    </div>
  </div>
);

// ---- FUTURISTIC CUSTOM BUILD (completely different composition) ----
const CustomLayout = () => (
  <div className="absolute inset-0 overflow-hidden bg-[#05050c]">
    <video className="absolute inset-0 w-full h-full object-cover opacity-60" autoPlay muted loop playsInline preload="auto"><source src="/custom-hero.mp4" type="video/mp4" /></video>
    <div className="absolute inset-0 bg-gradient-to-tr from-[#05050c] via-[#05050c]/40 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#05050c] via-transparent to-[#05050c]/60" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_25%,rgba(139,92,246,0.28),transparent_45%)]" />

    {/* vertical left rail */}
    <div className="absolute left-0 top-0 bottom-0 w-14 border-r border-white/10 bg-black/40 backdrop-blur flex flex-col items-center justify-between py-4 z-20">
      <span className="h-8 w-8 rounded-lg bg-gradient-to-tr from-teal to-violet flex items-center justify-center text-black"><Sparkle size={15} weight="fill" /></span>
      <div className="[writing-mode:vertical-rl] rotate-180 text-[10px] tracking-[0.3em] text-white/40 uppercase">Home · Work · Contact</div>
      <div className="flex flex-col gap-1.5">{[0, 1, 2].map((i) => <motion.span key={i} animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.2 }} className="h-1.5 w-1.5 rounded-full bg-teal" />)}</div>
    </div>

    {/* top-right floating widgets */}
    <div className="absolute top-5 right-5 flex items-start gap-3 z-20">
      <div className="rounded-xl border border-white/10 bg-white/[0.05] backdrop-blur p-3 w-32">
        <div className="flex items-center justify-between text-[10px] text-white/50">Revenue <span className="text-teal">+38%</span></div>
        <svg viewBox="0 0 120 40" className="mt-1 w-full h-8" fill="none">
          <motion.path initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.6 }} d="M2 34 L28 24 L52 28 L78 12 L118 4" stroke="url(#cg)" strokeWidth="2.5" strokeLinecap="round" />
          <defs><linearGradient id="cg" x1="0" x2="1"><stop offset="0" stopColor="#40E0D0" /><stop offset="1" stopColor="#8B5CF6" /></linearGradient></defs>
        </svg>
      </div>
      <span className="inline-flex items-center gap-1 rounded-full border border-teal/40 bg-teal/10 px-2.5 py-1 text-[10px] text-teal"><Sparkle weight="fill" size={10} /> AI online</span>
    </div>

    {/* floating rotating brand orb */}
    <motion.div animate={{ rotate: 360 }} transition={{ duration: 16, repeat: Infinity, ease: "linear" }} className="absolute top-24 right-24 h-24 w-24 rounded-full blur-[2px] z-10 hidden md:block" style={{ background: "conic-gradient(from 0deg,#40E0D0,#3B6EFF,#8B5CF6,#EC4899,#40E0D0)" }} />
    <div className="absolute top-[104px] right-[104px] h-16 w-16 rounded-full bg-[#05050c] z-10 hidden md:block" />

    {/* floating feature tags (diagonal, right) */}
    <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-2.5 z-20 items-end">
      {FEATURES.map((f, i) => { const Icon = f.icon; return (
        <motion.div key={f.t} animate={{ y: [0, -5, 0] }} transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }} style={{ marginRight: `${i * 18}px` }}
          className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.05] backdrop-blur px-3 py-1.5">
          <span className="h-6 w-6 rounded-md bg-gradient-to-br from-teal/30 to-cobalt/10 border border-teal/30 text-teal flex items-center justify-center"><Icon size={13} weight="duotone" /></span>
          <span className="text-xs text-white/85">{f.t}</span>
        </motion.div>
      ); })}
    </div>

    {/* GIANT headline anchored bottom-left */}
    <div className="absolute left-20 bottom-24 max-w-xl z-20">
      <div className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-white/70"><Lightning size={10} weight="fill" className="text-teal" /> AI-powered growth</div>
      <h3 className="mt-3 font-heading font-extrabold text-4xl md:text-5xl leading-[0.95] text-white">Grow your business<br /><span className="gradient-text">with confidence.</span></h3>
    </div>

    {/* bottom AI command bar */}
    <div className="absolute left-20 right-5 bottom-5 z-20 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-xl px-3 py-2 flex items-center gap-3">
      <ChatCircleDots size={16} weight="fill" className="text-teal ml-1" />
      <span className="flex-1 text-xs text-white/45 truncate">Ask Northwind AI to build a growth campaign…</span>
      <button className="inline-flex items-center gap-1.5 text-black text-xs font-semibold bg-gradient-to-r from-teal to-cobalt rounded-full px-4 py-2">Get started <ArrowRight size={12} weight="bold" /></button>
    </div>
  </div>
);

// ---- INTERACTIVE "Transform this site" toggle demo ----
const TransformDemo = () => {
  const [custom, setCustom] = useState(false);
  return (
    <div className="max-w-5xl mx-auto" data-testid="transform-demo">
      <div className="relative rounded-2xl border border-white/10 overflow-hidden shadow-glow-teal">
        {/* browser chrome */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10 bg-[#0b0b14]">
          <span className="h-2.5 w-2.5 rounded-full bg-magenta" />
          <span className="h-2.5 w-2.5 rounded-full bg-teal" />
          <span className="h-2.5 w-2.5 rounded-full bg-cobalt" />
          <span className="ml-3 h-5 flex-1 rounded-md bg-white/5 flex items-center px-3 text-[10px] text-white/40">northwindco.com</span>
          <span className={`text-[10px] uppercase tracking-[0.15em] transition-colors duration-500 ${custom ? "text-teal" : "text-white/40"}`}>{custom ? "Custom build" : "Template"}</span>
        </div>
        <div className="relative h-[440px] md:h-[520px] overflow-hidden">
          <AnimatePresence mode="wait">
            {custom ? (
              <motion.div key="c" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }} className="absolute inset-0"><CustomLayout /></motion.div>
            ) : (
              <motion.div key="t" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }} className="absolute inset-0"><TemplateLayout /></motion.div>
            )}
          </AnimatePresence>
          {/* scan sweep on toggle */}
          <AnimatePresence>
            <motion.div key={custom ? "sc" : "st"} initial={{ x: "-130%", opacity: 0.85 }} animate={{ x: "130%", opacity: 0 }} transition={{ duration: 0.95, ease: "easeInOut" }} className="absolute inset-y-0 -left-1/3 w-1/3 z-40 pointer-events-none skew-x-12 bg-gradient-to-r from-transparent via-teal/40 to-transparent" />
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-6 flex flex-col items-center gap-3">
        <motion.button
          onClick={() => setCustom((v) => !v)}
          whileTap={{ scale: 0.96 }}
          className={`inline-flex items-center gap-2 font-semibold rounded-full px-7 py-3.5 transition-colors duration-300 ${custom ? "text-white border border-white/20 hover:bg-white/10" : "text-black bg-gradient-to-r from-teal via-cobalt to-violet shadow-[0_10px_40px_-8px_rgba(59,110,255,0.6)]"}`}
          data-testid="transform-toggle-button"
        >
          <MagicWand size={18} weight="fill" className={custom ? "text-teal" : ""} />
          {custom ? "Reset to template" : "Transform this site"}
        </motion.button>
        <span className="text-xs text-white/40">Same business — {custom ? "completely reimagined as a custom AI build." : "click to see it custom-built."}</span>
      </div>
    </div>
  );
};

export const Manifesto = () => {
  return (
    <section id="manifesto" className="relative py-28 md:py-40 bg-transparent" data-testid="manifesto-section">
      {/* decorative glows in a clipped wrapper so they don't break sticky positioning */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -left-32 top-40 h-96 w-96 rounded-full bg-magenta/10 blur-[120px]" />
        <div className="absolute -right-32 bottom-20 h-96 w-96 rounded-full bg-cobalt/10 blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <Parallax speed={0.12} className="max-w-4xl">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-teal/80"><Lightning size={13} weight="fill" /> The problem with cookie-cutter</span>
          </Reveal>
          <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-white text-3xl sm:text-4xl md:text-5xl lg:text-[3.75rem] leading-[1.02]">
            <MaskLine>Templates make you bend</MaskLine>
            <MaskLine delay={0.08}>your business to fit</MaskLine>
            <MaskLine delay={0.16}><span className="text-white/40">the software.</span></MaskLine>
          </h2>
        </Parallax>
      </div>

      {/* full-bleed scroll expansion — must NOT live inside an overflow-hidden ancestor */}
      <div className="relative z-10 mt-14">
        <ScrollExpandMedia
          title="Go from template to custom"
          scrollToExpand="Scroll to expand"
          background={<TemplateLayout />}
          foreground={<CustomLayout />}
        >
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              The same business, rebuilt. A cookie-cutter template becomes a custom
              site engineered around how you actually work — no theme limits, AI
              automations baked in, and it&apos;s 100% yours.
            </p>
          </div>
        </ScrollExpandMedia>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 mt-20 grid md:grid-cols-3 gap-6">
        {CHAPTERS.map((c, i) => {
          const a = ACCENT[c.accent];
          const Icon = c.icon;
          return (
            <motion.div key={c.n} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }} whileHover={{ y: -6 }}
              className={`group relative rounded-2xl border border-white/10 bg-[#0A0A12]/80 backdrop-blur-sm p-8 overflow-hidden transition-shadow duration-500 ${a.glow}`} data-testid={`highlight-${c.n}`}>
              <span className="absolute right-5 top-4 font-heading text-6xl font-extrabold text-white/[0.06] group-hover:text-white/[0.1] transition-colors">{c.n}</span>
              <motion.div whileHover={{ rotate: -8, scale: 1.08 }} transition={{ type: "spring", stiffness: 300, damping: 12 }} className={`relative h-14 w-14 rounded-2xl border bg-gradient-to-br flex items-center justify-center ${a.tile}`}><Icon size={26} weight="duotone" /></motion.div>
              <div className="mt-6 h-10 flex items-center"><Motif type={c.motif} /></div>
              <h3 className="mt-6 font-heading text-2xl font-bold text-white">{c.title}</h3>
              <p className="mt-3 text-white/60 leading-relaxed">{c.body}</p>
              <div className={`mt-6 h-1 w-0 group-hover:w-full rounded-full ${a.dot} transition-all duration-500`} />
            </motion.div>
          );
        })}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 mt-24">
        <Reveal className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-teal/80"><MagicWand size={13} weight="fill" /> Try it yourself</span>
          <h3 className="mt-4 font-heading font-extrabold text-white text-2xl sm:text-3xl md:text-4xl">See the transformation on demand.</h3>
        </Reveal>
        <TransformDemo />
      </div>
    </section>
  );
};
